import { Link, useParams, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import './LandingPage.css';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  white:  '#fff',
  font:   "'Inter', system-ui, sans-serif",
};

/* ── All types (for sidebar navigation + related cards) ── */
const ALL_TYPES = [
  {
    slug: 'mehrfamilienhaus',
    label: 'Mehrfamilienhaus',
    sub: 'Bis zu 50 Einheiten',
    img: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80&fit=crop&auto=format',
  },
  {
    slug: 'einfamilienhaus',
    label: 'Einfamilienhaus',
    sub: 'Privat & kompakt',
    img: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=600&q=80&fit=crop&auto=format',
  },
  {
    slug: 'eigentumswohnung',
    label: 'Eigentumswohnung',
    sub: 'ETW & Eigentümer',
    img: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=600&q=80&fit=crop&auto=format',
  },
  {
    slug: 'gewerbeimmobilie',
    label: 'Gewerbeimmobilie',
    sub: 'Büro & Laden',
    img: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=600&q=80&fit=crop&auto=format',
  },
  {
    slug: 'neubauprojekt',
    label: 'Neubauprojekt',
    sub: 'Erstbezug & Planung',
    img: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=600&q=80&fit=crop&auto=format',
  },
  {
    slug: 'dachgeschoss',
    label: 'Dachgeschoss',
    sub: 'Penthouse & Maisonette',
    img: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=600&q=80&fit=crop&auto=format',
  },
  {
    slug: 'kapitalanlage',
    label: 'Kapitalanlage',
    sub: 'Rendite & Portfolio',
    img: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80&fit=crop&auto=format',
  },
];

const DATA = {
  mehrfamilienhaus: {
    label:   'Mehrfamilienhaus',
    tagline: 'Kein Zahlungsrückstand geht mehr verloren — ImmoHQ übernimmt Mahnwesen, Abrechnung und Mieterportal vollautomatisch.',
    img:     'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1600&q=85&fit=crop&auto=format',
    stats: [
      { num: 'Bis 50',  label: 'Einheiten pro Objekt' },
      { num: '< 5min',  label: 'Einrichtung pro Einheit' },
      { num: '100%',    label: 'digital & papierlos' },
    ],
    tasks: [
      'Mieteingänge monatlich automatisch prüfen und erfassen',
      'Mahnungen bei Zahlungsverzug automatisch eskalieren',
      'Betriebskostenabrechnung für alle Einheiten erstellen',
      'Mieter per E-Mail ins eigene Portal einladen',
      'Dokumente, Verträge und Protokolle zentral archivieren',
    ],
    scenarios: [
      { title: 'Mietzahlung fehlt', desc: 'Ein Mieter hat im März nicht gezahlt. ImmoHQ erkennt den offenen Posten automatisch und versendet nach 3 Tagen eine erste Mahnung — ohne dein Zutun.' },
      { title: 'Jahresabrechnung fällig', desc: 'Die Betriebskostenabrechnung steht an. Du trägst die Kosten ein, ImmoHQ verteilt sie auf alle Einheiten und erstellt PDFs für jeden Mieter.' },
      { title: 'Mieter zieht aus', desc: 'Mieter meldet Auszug. Du dokumentierst die Übergabe, rechnest die Kaution ab und archivierst alle Unterlagen — in einem System.' },
    ],
    results: [
      'Kein manuelles Mahnwesen mehr',
      'Betriebskosten in Minuten erstellt',
      'Alle Mieter digital erreichbar',
      'Vollständige Dokumentation jederzeit',
    ],
  },
  einfamilienhaus: {
    label:   'Einfamilienhaus',
    tagline: 'Auch mit einer einzigen Einheit profitierst du von klaren Prozessen — von der Mietzahlung bis zur Kautionsabrechnung.',
    img:     'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=1600&q=85&fit=crop&auto=format',
    stats: [
      { num: '1–5',     label: 'Einheiten ideal' },
      { num: '< 3min',  label: 'Einrichtung komplett' },
      { num: 'DSGVO',   label: 'konform & sicher' },
    ],
    tasks: [
      'Monatliche Mietzahlung erfassen und bestätigen',
      'Kautionen rechtssicher dokumentieren und verwalten',
      'Mietvertrag und Übergabeprotokoll digital ablegen',
      'Zahlungsverzug automatisch erkennen und mahnen',
      'Betriebskosten einfach erfassen und abrechnen',
    ],
    scenarios: [
      { title: 'Erste Vermietung', desc: 'Du vermietest zum ersten Mal. Objekt anlegen, Mieter hinzufügen, Mietvertrag hochladen — in 5 Minuten ist alles eingerichtet.' },
      { title: 'Miete kommt zu spät', desc: 'Der Mieter zahlt regelmäßig 2–3 Tage zu spät. ImmoHQ trackt den Eingang und schickt automatisch eine Erinnerung, wenn der Termin überschritten wird.' },
      { title: 'Kaution zurückgeben', desc: 'Mietverhältnis endet. Du dokumentierst den Zustand, berechnest eventuelle Abzüge und gibst die Kaution korrekt zurück — alles in ImmoHQ.' },
    ],
    results: [
      'Keine Excel-Tabellen mehr',
      'Kaution jederzeit korrekt dokumentiert',
      'Alle Dokumente digital & sicher',
      'Mahnungen automatisch versandt',
    ],
  },
  eigentumswohnung: {
    label:   'Eigentumswohnung',
    tagline: 'Eine ETW vermieten ist mehr als einmal Mietvertrag unterschreiben — ImmoHQ kümmert sich um den Rest.',
    img:     'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1600&q=85&fit=crop&auto=format',
    stats: [
      { num: 'ETW',     label: 'vollständig verwaltet' },
      { num: 'PDF',     label: 'Abrechnung auf Klick' },
      { num: '24/7',    label: 'Mieterportal verfügbar' },
    ],
    tasks: [
      'Mietvertrag mit Laufzeit und Staffelmiete hinterlegen',
      'Nebenkosten erfassen und Jahresabrechnung erstellen',
      'Mieteranfragen und Reparaturen zentral verwalten',
      'Kautionsabrechnung rechtssicher dokumentieren',
      'Alle Dokumente DSGVO-konform digital archivieren',
    ],
    scenarios: [
      { title: 'Nebenkostenabrechnung', desc: 'Jahresende: Du gibst alle Kostenposten ein. ImmoHQ berechnet den Anteil des Mieters, erstellt das PDF und stellt es im Mieterportal bereit.' },
      { title: 'Reparaturanfrage', desc: 'Mieter meldet defekte Heizung über das Portal. Du siehst die Anfrage sofort, koordinierst den Handwerker und dokumentierst alles in ImmoHQ.' },
      { title: 'Staffelmiete greift', desc: 'Die vertraglich festgelegte Staffelerhöhung wird fällig. ImmoHQ erinnert dich rechtzeitig und passt den Sollbetrag automatisch an.' },
    ],
    results: [
      'Nebenkostenabrechnung in Minuten',
      'Mieterportal inklusive',
      'Keine verpassten Staffelmieten',
      'Lückenlose Dokumentation',
    ],
  },
  gewerbeimmobilie: {
    label:   'Gewerbeimmobilie',
    tagline: 'Büro, Laden oder Praxis — Gewerbeimmobilien haben eigene Anforderungen. ImmoHQ bildet sie alle ab.',
    img:     'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1600&q=85&fit=crop&auto=format',
    stats: [
      { num: 'Multi',   label: 'Mieter pro Objekt' },
      { num: 'Flex',    label: 'Mietstrukturen' },
      { num: 'PDF',     label: 'Abrechnung pro Mieter' },
    ],
    tasks: [
      'Mehrere Gewerbeflächen in einem Objekt verwalten',
      'Staffel- und Indexmieten korrekt erfassen',
      'Umlagefähige Betriebskosten auf Mieter verteilen',
      'Leerstand und Auslastung in Echtzeit tracken',
      'Dokumente und Verträge pro Einheit archivieren',
    ],
    scenarios: [
      { title: 'Mehrere Mieter, ein Gebäude', desc: 'Dein Bürogebäude hat 5 Mieter. Jeder hat seine eigene Fläche, Miete und Abrechnung — ImmoHQ behält den Überblick für alle gleichzeitig.' },
      { title: 'Indexmiete wird angepasst', desc: 'Der Verbraucherpreisindex steigt. ImmoHQ erinnert dich an die Anpassung und berechnet den neuen Mietzins nach hinterlegter Formel.' },
      { title: 'Einheit wird frei', desc: 'Ein Gewerbemieter zieht aus. Du siehst die freie Fläche sofort in der Übersicht, dokumentierst die Übergabe und bereitest die Neuvermietung vor.' },
    ],
    results: [
      'Alle Flächen zentral im Blick',
      'Korrekte Betriebskostenverteilung',
      'Leerstand sofort erkennbar',
      'Jeder Mieter individuell verwaltet',
    ],
  },
  neubauprojekt: {
    label:   'Neubauprojekt',
    tagline: 'Erstbezug strukturieren, Einzüge koordinieren, Dokumente von Anfang an richtig ablegen — mit ImmoHQ.',
    img:     'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1600&q=85&fit=crop&auto=format',
    stats: [
      { num: 'Tag 1',   label: 'direkt startklar' },
      { num: 'Digital', label: 'Einzugsprozess' },
      { num: 'Skaliert', label: 'mit deinem Projekt' },
    ],
    tasks: [
      'Alle Einheiten anlegen und Einzugstermine erfassen',
      'Mieter digital einladen und Daten selbst eintragen lassen',
      'Übergabeprotokolle digital erstellen und signieren',
      'Mietverträge und Erstdokumente sofort archivieren',
      'Zahlungen ab dem ersten Monat automatisch tracken',
    ],
    scenarios: [
      { title: 'Erstbezug steht bevor', desc: '8 Wohnungen beziehen gleichzeitig ein. Du legst alle Einheiten an, lädst die Mieter digital ein — sie tragen ihre Daten selbst ein. Kein Papierchaos.' },
      { title: 'Übergabe dokumentieren', desc: 'Einzug in Wohnung 4. Du erfasst Zählerstände, Mängel und Schlüsselübergabe direkt in ImmoHQ — alles gespeichert, nichts vergessen.' },
      { title: 'Zweite Bauphase', desc: 'Das Projekt wächst auf 20 Einheiten. Du erweiterst deinen Plan in ImmoHQ — alle bisherigen Daten bleiben erhalten, kein Systemwechsel nötig.' },
    ],
    results: [
      'Kein Papierchaos beim Einzug',
      'Alle Dokumente von Tag 1 digital',
      'Mieterportal sofort aktiv',
      'Skaliert ohne Mehraufwand',
    ],
  },
  dachgeschoss: {
    label:   'Dachgeschoss',
    tagline: 'Hochwertige Einheiten verdienen professionelle Verwaltung — ImmoHQ liefert den passenden Standard.',
    img:     'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=1600&q=85&fit=crop&auto=format',
    stats: [
      { num: 'Premium', label: 'Verwaltung' },
      { num: 'PDF',     label: 'Dokumente auf Abruf' },
      { num: 'Diskret', label: 'Mieterportal' },
    ],
    tasks: [
      'Sonderflächen wie Terrassen und Galerien erfassen',
      'Hohe Kautionen korrekt dokumentieren und verzinsen',
      'Professionelle Abrechnungen als PDF erstellen',
      'Mieterportal als diskreten Kommunikationskanal nutzen',
      'Alle Dokumente sicher und strukturiert archivieren',
    ],
    scenarios: [
      { title: 'Kaution über Standardhöhe', desc: 'Dein Mieter zahlt eine höhere Kaution. ImmoHQ dokumentiert Betrag, Eingang und Verzinsung — rechtssicher und ohne Tabellenkalkulationen.' },
      { title: 'Mieter stellt Anfrage', desc: 'Der Mieter hat eine Frage zur Abrechnung. Er öffnet das Portal, sieht alle Unterlagen und schickt eine Nachricht — ohne deine persönliche Handynummer.' },
      { title: 'Jahresabrechnung', desc: 'Die Betriebskostenabrechnung für die Penthouse-Einheit ist fällig. Du erstellst das PDF in Minuten — korrekt formatiert und direkt versandfertig.' },
    ],
    results: [
      'Professioneller Auftritt gegenüber Mietern',
      'Kaution lückenlos dokumentiert',
      'Kein direkter Kontaktdruck',
      'Abrechnungen schnell & korrekt',
    ],
  },
  kapitalanlage: {
    label:   'Kapitalanlage',
    tagline: 'Immobilien als Investment — ImmoHQ liefert die Zahlen, die du für fundierte Entscheidungen brauchst.',
    img:     'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&q=85&fit=crop&auto=format',
    stats: [
      { num: 'Echtzeit', label: 'Cashflow-Übersicht' },
      { num: 'Auto',     label: 'Mahnwesen & Prozesse' },
      { num: 'Skaliert', label: 'bis 100+ Einheiten' },
    ],
    tasks: [
      'Einnahmen und Ausgaben pro Objekt in Echtzeit tracken',
      'Mahnwesen und Prozesse vollständig automatisieren',
      'Portfolio über mehrere Objekte hinweg verwalten',
      'Steuervorbereitung mit vollständigen Ausgabenlisten',
      'Leerstandsquote und Auslastung überwachen',
    ],
    scenarios: [
      { title: 'Monatlicher Cashflow-Check', desc: 'Du willst wissen, wie April gelaufen ist. Ein Blick aufs Dashboard zeigt Einnahmen, offene Posten und Ausgaben — alle Objekte auf einmal.' },
      { title: 'Portfolio wächst', desc: 'Du kaufst ein drittes Objekt. Einrichten dauert 5 Minuten, alle bestehenden Daten bleiben — und die Übersicht bleibt genauso klar wie zuvor.' },
      { title: 'Steuererklärung vorbereiten', desc: 'Der Steuerberater braucht alle Einnahmen und Kosten. Du exportierst die komplette Aufstellung aus ImmoHQ — in Sekunden, für jedes Objekt einzeln.' },
    ],
    results: [
      'Cashflow immer transparent',
      'Kein manueller Verwaltungsaufwand',
      'Steuervorbereitung in Minuten',
      'Portfolio skaliert ohne Mehraufwand',
    ],
  },
};

export default function ImmobilienType() {
  const { type } = useParams();
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const d = DATA[type];
  if (!d) return <Navigate to="/" replace />;

  const related = ALL_TYPES.filter(t => t.slug !== type).slice(0, 4);

  return (
    <div style={{ fontFamily: T.font, color: T.ink, background: T.white, overflowX: 'hidden' }}>

      {/* ── Nav ── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 200,
        background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${T.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 clamp(16px,5vw,40px)', height: 64,
        boxShadow: '0 1px 12px rgba(0,0,0,0.06)',
      }}>
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, background: T.ink, borderRadius: 9,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.3px', color: T.ink }}>ImmoHQ</span>
        </Link>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Link to="/login" style={{
            padding: '9px 18px', border: `1.5px solid ${T.border}`,
            borderRadius: 8, fontSize: 14, fontWeight: 600, color: T.ink,
            background: T.white, textDecoration: 'none',
          }}>Anmelden</Link>
          <Link to="/register" style={{
            padding: '9px 20px', background: T.ink, color: T.white,
            borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap',
          }}>Kostenlos starten</Link>
        </div>
      </nav>

      {/* ── HERO — full bleed ── */}
      <div style={{ position: 'relative', height: 'clamp(400px,52vw,600px)', overflow: 'hidden' }}>
        <img
          src={d.img}
          alt={d.label}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {/* Dark overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to right, rgba(0,0,0,0.82) 45%, rgba(0,0,0,0.25) 100%)',
        }} />

        <div style={{
          position: 'absolute', inset: 0,
          padding: 'clamp(24px,4vw,48px) clamp(16px,5vw,56px)',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          maxWidth: 720,
        }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
            <Link to="/" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>Home</Link>
            <span>/</span>
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Immobilien</span>
            <span>/</span>
            <span style={{ color: 'rgba(255,255,255,0.85)' }}>{d.label}</span>
          </div>

          {/* Pill */}
          <div style={{
            display: 'inline-flex', alignItems: 'center',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 6, padding: '4px 12px', marginBottom: 18,
            fontSize: 13, fontWeight: 600, color: T.white,
            width: 'fit-content',
          }}>
            {d.label}
          </div>

          {/* Headline */}
          <h1 style={{
            fontSize: 'clamp(36px,5.5vw,68px)', fontWeight: 900,
            lineHeight: 1.06, letterSpacing: '-2px', color: T.white,
            margin: '0 0 18px',
          }}>
            ImmoHQ für<br />{d.label}.
          </h1>

          {/* Subtext */}
          <p style={{ fontSize: 'clamp(14px,1.5vw,17px)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.65, margin: '0 0 28px', maxWidth: 520 }}>
            {d.tagline}
          </p>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/register" style={{
              padding: '13px 26px', background: T.white, color: T.ink,
              borderRadius: 8, fontSize: 14, fontWeight: 800, textDecoration: 'none',
            }}>
              Kostenlos starten →
            </Link>
            <Link to="/#how-it-works" style={{
              padding: '12px 26px', background: 'transparent', color: T.white,
              border: '1.5px solid rgba(255,255,255,0.35)',
              borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none',
            }}>
              So funktioniert's
            </Link>
          </div>
        </div>
      </div>

      {/* ── STATS STRIP ── */}
      <div style={{ borderBottom: `1px solid ${T.border}` }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(3,1fr)',
        }} className="lp-stat-grid">
          {d.stats.map(({ num, label }, i) => (
            <div key={i} style={{
              padding: 'clamp(24px,3vw,36px) clamp(20px,4vw,48px)',
              borderRight: i < 2 ? `1px solid ${T.border}` : 'none',
              display: 'flex', alignItems: 'baseline', gap: 14,
            }}>
              <span style={{ fontSize: 'clamp(32px,4vw,48px)', fontWeight: 900, letterSpacing: '-2px', color: T.ink, lineHeight: 1 }}>
                {num}
              </span>
              <span style={{ fontSize: 14, color: T.muted, lineHeight: 1.4 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── MAIN CONTENT + SIDEBAR ── */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: 'clamp(40px,6vw,72px) clamp(16px,5vw,40px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 56, alignItems: 'start' }} className="lp-2col-grid">

          {/* ── LEFT — main content ── */}
          <div>

            {/* WAS IMMOHQ ÜBERNIMMT */}
            <div style={{ marginBottom: 56 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: T.muted, margin: '0 0 10px' }}>
                WAS IMMOHQ ÜBERNIMMT
              </p>
              <h2 style={{ fontSize: 'clamp(24px,3vw,38px)', fontWeight: 900, letterSpacing: '-1px', color: T.ink, margin: '0 0 28px', lineHeight: 1.15 }}>
                Was {d.label} mit ImmoHQ<br />automatisieren.
              </h2>

              <div style={{ border: `1.5px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
                {d.tasks.map((task, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    padding: '18px 22px',
                    borderBottom: i < d.tasks.length - 1 ? `1px solid ${T.border}` : 'none',
                  }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%',
                      background: T.ink, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.white }}>{i + 1}</span>
                    </div>
                    <span style={{ fontSize: 15, color: T.ink, fontWeight: 500, lineHeight: 1.4 }}>{task}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* SO SIEHT ES IN DER PRAXIS AUS */}
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: T.muted, margin: '0 0 10px' }}>
                SO SIEHT ES IN DER PRAXIS AUS
              </p>
              <h2 style={{ fontSize: 'clamp(22px,2.5vw,34px)', fontWeight: 900, letterSpacing: '-0.8px', color: T.ink, margin: '0 0 28px', lineHeight: 1.15 }}>
                Drei Alltagssituationen.
              </h2>

              {d.scenarios.map(({ title, desc }, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 22, alignItems: 'flex-start',
                  padding: '24px 0',
                  borderBottom: i < d.scenarios.length - 1 ? `1px solid ${T.border}` : 'none',
                }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: T.ink, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.white }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.ink, marginBottom: 6 }}>{title}</div>
                    <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.65 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>

          </div>

          {/* ── RIGHT SIDEBAR ── */}
          <div style={{ position: 'sticky', top: 80, display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* CTA Card */}
            <div style={{
              background: T.ink, borderRadius: 16, padding: '28px 24px',
            }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: T.white, marginBottom: 6 }}>Jetzt starten</div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.55, margin: '0 0 20px' }}>
                14 Tage kostenlos testen — keine Kreditkarte, kein Risiko.
              </p>
              <Link to="/register" style={{
                display: 'block', textAlign: 'center',
                padding: '12px', background: T.white, color: T.ink,
                borderRadius: 8, fontSize: 14, fontWeight: 800, textDecoration: 'none',
              }}>
                Kostenlos starten
              </Link>
            </div>

            {/* All types list */}
            <div style={{ border: `1.5px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: T.muted }}>
                  ALLE IMMOBILIENTYPEN
                </span>
              </div>
              {ALL_TYPES.map(t => {
                const active = t.slug === type;
                return (
                  <Link
                    key={t.slug}
                    to={`/immobilien/${t.slug}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '13px 18px',
                      borderBottom: `1px solid ${T.border}`,
                      background: active ? T.ink : T.white,
                      textDecoration: 'none',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = T.bg; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = T.white; }}
                  >
                    <span style={{ fontSize: 14, fontWeight: active ? 700 : 500, color: active ? T.white : T.ink }}>
                      {t.label}
                    </span>
                    {active && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    )}
                  </Link>
                );
              })}
              <Link to="/" style={{
                display: 'block', padding: '13px 18px',
                fontSize: 13, fontWeight: 600, color: T.muted,
                textDecoration: 'none', textAlign: 'center',
              }}>
                Alle ansehen →
              </Link>
            </div>

          </div>
        </div>
      </div>

      {/* ── RESULTS — dark card ── */}
      <div style={{ padding: '0 clamp(16px,5vw,40px) clamp(40px,6vw,64px)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{
            background: T.ink, borderRadius: 20,
            padding: 'clamp(32px,4vw,52px)',
            maxWidth: 840,
          }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', margin: '0 0 14px' }}>
              DAS ERGEBNIS
            </p>
            <h2 style={{ fontSize: 'clamp(22px,2.8vw,36px)', fontWeight: 900, color: T.white, letterSpacing: '-0.8px', margin: '0 0 28px', lineHeight: 1.2 }}>
              {d.results[0].replace(' mehr', '.')} {d.results[1].replace(' in Minuten', '.')}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }} className="lp-2col-grid">
              {d.results.map((r, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10, padding: '14px 16px',
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', fontWeight: 500, lineHeight: 1.4 }}>{r}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── RELATED ── */}
      <section style={{ padding: 'clamp(40px,6vw,72px) clamp(16px,5vw,40px)', background: T.bg }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(22px,2.5vw,34px)', fontWeight: 900, letterSpacing: '-0.8px', color: T.ink, margin: '0 0 28px' }}>
            Ähnliche Immobilientypen
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }} className="lp-feat-grid">
            {related.map(t => (
              <Link key={t.slug} to={`/immobilien/${t.slug}`} style={{ textDecoration: 'none' }}>
                <div style={{
                  borderRadius: 14, overflow: 'hidden',
                  background: T.white, border: `1.5px solid ${T.border}`,
                  transition: 'box-shadow 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                >
                  <div style={{ height: 160, overflow: 'hidden' }}>
                    <img
                      src={t.img}
                      alt={t.label}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </div>
                  <div style={{ padding: '16px' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginBottom: 4 }}>{t.label}</div>
                    <div style={{ fontSize: 13, color: T.muted }}>{t.sub}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
